import { useEffect, useRef } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { SiteLayout } from './components/layout.js';
import { CandidacyDetailPage, CandidaciesPage } from './features/candidacies/index.js';
import { ComparisonPage } from './features/comparison/index.js';
import { DashboardPage } from './features/dashboard/index.js';
import { ElectionExperience } from './features/elections/experience.js';
import { PartiesPage, PartyDetailPage } from './features/parties/index.js';
import { PoliticiansPage } from './features/politicians/index.js';
import { ProfilePage } from './features/profiles/index.js';
import { PresidentsPage } from './features/presidents/index.js';
import { PresidentialProfilePage } from './features/presidents/profile.js';
import { ComparePresidentsPage } from './features/presidents/compare.js';
import { TaxRevenuePage } from './features/revenue/index.js';
import { MethodologyPage, PrivacyPage, SourcesPage } from './features/sources/index.js';
import { applyClientSeo, createPageSeo } from './seo.js';

function PublicLayout() {
  const location = useLocation();
  const initialLocation = useRef<string | null>(typeof window === 'undefined' ? null : `${window.location.pathname}${window.location.search}`);
  useEffect(() => {
    const currentLocation = `${location.pathname}${location.search}`;
    if (initialLocation.current === currentLocation) {
      initialLocation.current = null;
      return;
    }
    initialLocation.current = null;
    applyClientSeo(createPageSeo(location.pathname, location.search));
  }, [location.pathname, location.search]);
  return <SiteLayout><Outlet /></SiteLayout>;
}

export function App() {
  return <Routes>
    <Route path="/dashboard" element={<DashboardPage />} />
    <Route path="/painel" element={<Navigate to="/dashboard" replace />} />
    <Route path="/administracao" element={<Navigate to="/dashboard" replace />} />
    <Route element={<PublicLayout />}>
      <Route path="/" element={<Navigate to="/apuracao_eleicoes_2026_segundo_turno" replace />} />
      <Route path="/apuracao_eleicoes_2026_segundo_turno" element={<ElectionExperience turno={2} />} />
      <Route path="/apuracao_eleicoes_2026_segundo_turno/:uf" element={<SecondTurnStateRedirect />} />
      <Route path="/apuracao_eleicoes_2026_segundo_turno_backup" element={<SecondTurnBackupRedirect />} />
      <Route path="/apuracao_eleicoes_2026_primeiro_turno" element={<ElectionExperience turno={1} />} />
      <Route path="/apuracao" element={<Navigate to="/apuracao_eleicoes_2026_segundo_turno" replace />} />
      <Route path="/politicos" element={<PoliticiansPage />} />
      <Route path="/explorar" element={<Navigate to="/politicos" replace />} />
      <Route path="/partidos" element={<PartiesPage />} />
      <Route path="/partidos/:party" element={<PartyDetailPage />} />
      <Route path="/candidaturas" element={<CandidaciesPage />} />
      <Route path="/candidaturas/:id" element={<CandidacyDetailPage />} />
      <Route path="/presidentes" element={<PresidentsPage />} />
      <Route path="/presidentes/comparar" element={<ComparePresidentsPage />} />
      <Route path="/presidentes/:slug" element={<PresidentialProfilePage />} />
      <Route path="/perfil/:id" element={<ProfilePage />} />
      <Route path="/politicos/:id" element={<LegacyProfileRedirect />} />
      <Route path="/comparar" element={<ComparisonPage />} />
      <Route path="/impostometro" element={<TaxRevenuePage />} />
      <Route path="/comparacao" element={<Navigate to="/comparar" replace />} />
      <Route path="/fontes" element={<SourcesPage />} />
      <Route path="/metodologia" element={<MethodologyPage />} />
      <Route path="/privacidade" element={<PrivacyPage />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>;
}

function SecondTurnStateRedirect() {
  const { uf = '' } = useParams();
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('map_uf', uf.toUpperCase());
  next.set('uf', uf.toUpperCase());
  return <Navigate to={`/apuracao_eleicoes_2026_segundo_turno?${next.toString()}`} replace />;
}

function SecondTurnBackupRedirect() {
  const location = useLocation();
  return <Navigate to={`/apuracao_eleicoes_2026_segundo_turno${location.search}`} replace />;
}

function LegacyProfileRedirect() {
  const location = useLocation();
  const id = location.pathname.split('/').at(-1);
  return <Navigate to={`/perfil/${id}${location.search}`} replace />;
}

function NotFound() {
  return <section className="panel empty-state"><p className="eyebrow">404 · PÁGINA NÃO ENCONTRADA</p><h1>Não encontramos esta página</h1><p>Confira o endereço ou volte à consulta dos representantes.</p><a className="primary" href="/politicos">Explorar políticos</a></section>;
}

